/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    Address,
    AddressPurpose,
    AttributeValueBase,
    Cart,
    CartLine,
    CountryRegionInfo,
    DeliveryOption,
    FilterDeliveryModeOption,
    // ProductDeliveryOptions,
    StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, when } from 'mobx';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    getCartLinesforDelivery,
    getCartLinesforPickup,
    getCartLinesforSubscription,
    updateLineDeliverySpecifications
} from '../../helpers/utilities/cart-line-utilities';
import { ICheckoutState } from '@msdyn365-commerce/global-state';
import { ILdsCheckoutData } from './lds-checkout.data';
import { ILdsCheckoutProps } from './lds-checkout.props.autogenerated';
import { getOrderSummary, IOrderSummary } from './components/get-order-summary';
import { getInvoicePaymentSummary, IInvoicePaymentSummary } from './components/get-invoice-payment-summary';
import { submitAddress, getSmartyStreetAddress } from '../../helpers/utilities/address-utilities';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/core';
import { ILdsDeliveryResources } from '../lds-delivery/lds-delivery.props.autogenerated';
import { ICartLineWithProduct } from '../lds-delivery';
import { getDeliveryOptionsAsync, updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { IChurchRetailChannelCountryEntity, ICPBCustomerSubscriptionsEntity } from '../../actions/DataServiceEntities.g';

export interface ILdsCheckoutModuleProps extends ILdsCheckoutProps<ILdsCheckoutData>, IModuleStateProps {}

export interface ILdsCheckoutState {
    checkout?: ICheckoutState;
    address?: Address[];
    countryRegions?: CountryRegionInfo[];
    addressPurposes?: AddressPurpose[];
    countryStates?: StateProvinceInfo[];
    deliveryOptions?: DeliveryOption[];
    shippingCartLines?: CartLine[];
    // orgUnitLocations?: OrgUnitLocation[];
    // channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    // featureState?: FeatureState[];
    // storeSelectorStateManager?: IStoreSelectorStateManager;
    currentCart?: Cart;
    churchRetailChannelCountry?: IChurchRetailChannelCountryEntity[];
    customerSubscriptions?: ICPBCustomerSubscriptionsEntity[];
    selectedDelivery?: [];
    checkoutError?: boolean;
}
export interface ILdsCheckoutViewProps extends ILdsCheckoutProps<ILdsCheckoutData> {
    items: React.ReactNode[];
    checkout?: ICheckoutState;
    checkoutError?: boolean;
    address?: Address[];
    countryRegions?: CountryRegionInfo[];
    addressPurposes?: AddressPurpose[];
    countryStates?: StateProvinceInfo[];
    deliveryOptions?: DeliveryOption[];
    // orgUnitLocations?: OrgUnitLocation[];
    // channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    // featureState?: FeatureState[];
    // storeSelectorStateManager?: IStoreSelectorStateManager;
    shippingCartLines: CartLine[];
    subscriptionLines: CartLine[];
    pickupLines: CartLine[];
    customerSubscriptions?: ICPBCustomerSubscriptionsEntity[];
    hasInvoiceLine?: boolean;
    isPhoneRequired: boolean;
    orderSummary?: IOrderSummary;
    invoicePaymentSummary?: IInvoicePaymentSummary;
    saveShippingAddress: (
        context: ICoreContext,
        resources: ILdsDeliveryResources,
        telemetry: ITelemetry,
        addressToSave: Address,
        customerAddresses: Address[],
        cartId: string,
        shippingGroup: ICartLineWithProduct[],
        addAddressToAccount: boolean
    ) => void;
    getSmartyAddress: (context: ICoreContext, address: Address) => Promise<any>;
    setDeliveryOptionOnShippingCartLines: (deliveryOption: DeliveryOption | undefined) => Promise<Cart>;
    setContactOnShippingCartLines: (email: string | undefined, phone: string | undefined) => Promise<any>;
    updateCartAttributes: (attributes: AttributeValueBase[]) => Promise<any>;
    updateCurrentCart: (newCart: Cart) => void;
    updateCheckoutError: (value: boolean) => void;
}

/**
 *
 * LdsCheckout component.
 * @extends {React.Component<ILdsCheckoutModuleProps>}
 */
// @ts-expect-error
@withModuleState
@observer
class LdsCheckout extends React.Component<ILdsCheckoutModuleProps, ILdsCheckoutState> {
    @computed get isDataReady(): boolean {
        const checkoutData = (this.props.data.checkout?.result && this.props.data.checkout.status) === 'SUCCESS';
        const addressData = (this.props.data.address?.result && this.props.data.address.status) === 'SUCCESS';
        const countryRegionsData = (this.props.data.countryRegions?.result && this.props.data.countryRegions.status) === 'SUCCESS';
        const addressPurposesData = (this.props.data.addressPurposes?.result && this.props.data.addressPurposes.status) === 'SUCCESS';
        const countryStatesData = (this.props.data.countryStates?.result && this.props.data.countryStates.status) === 'SUCCESS';
        const deliveryOptionsData = (this.props.data.deliveryOptions?.result && this.props.data.deliveryOptions.status) === 'SUCCESS';
        const customerSubscriptionsData =
            (this.props.data.customerSubscriptions?.result && this.props.data.customerSubscriptions.status) === 'SUCCESS';
        const churchRetailChannelCountryData =
            (this.props.data.churchRetailChannelCountry?.result && this.props.data.churchRetailChannelCountry.status) === 'SUCCESS';

        const isReady =
            checkoutData &&
            addressData &&
            countryRegionsData &&
            addressPurposesData &&
            countryStatesData &&
            deliveryOptionsData &&
            customerSubscriptionsData &&
            churchRetailChannelCountryData;

        return isReady;
    }

    constructor(props: ILdsCheckoutModuleProps) {
        super(props);
        this.state = {
            checkout: undefined,
            address: [],
            countryRegions: [],
            addressPurposes: [],
            countryStates: [],
            deliveryOptions: [],
            shippingCartLines: [],
            currentCart: undefined,
            churchRetailChannelCountry: [],
            customerSubscriptions: [],
            selectedDelivery: [],
            checkoutError: false
        };
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );
    }

    public shouldComponentUpdate(nextProps: ILdsCheckoutModuleProps, nextState: ILdsCheckoutState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        if (!this.isDataReady) {
            return null;
        }
        const items = this.getItems();

        if (items.length === 0) {
            this.props.context.telemetry.error('Checkout content is empty, module wont render');
            return null;
        }

        let isPhoneRequired = false;
        if (this.state.churchRetailChannelCountry && this.state.churchRetailChannelCountry.length > 0) {
            const phoneNumberReq = this.state.churchRetailChannelCountry[0].PHONENUMBERREQ;
            isPhoneRequired = phoneNumberReq === 1;
        }

        const subscriptionLines = this.getSubscriptionLines();
        const pickupLines = this.getPickupLines();

        const hasInvoiceLine = this.state.checkout?.checkoutCart.hasInvoiceLine;
        const orderSummary = getOrderSummary(this.props, this.state.currentCart);
        const invoicePaymentSummary = getInvoicePaymentSummary(this.props);

        const viewProps = {
            ...this.props,
            items,
            checkout: this.state.checkout,
            checkoutError: this.state.checkoutError,
            address: this.state.address,
            countryRegions: this.state.countryRegions,
            addressPurposes: this.state.addressPurposes,
            countryStates: this.state.countryStates,
            deliveryOptions: this.state.deliveryOptions,
            shippingCartLines: this.state.shippingCartLines,
            customerSubscriptions: this.state.customerSubscriptions,
            isPhoneRequired,
            subscriptionLines,
            pickupLines,
            hasInvoiceLine,
            orderSummary,
            invoicePaymentSummary,
            saveShippingAddress: this.saveShippingAddress,
            setDeliveryOptionOnShippingCartLines: this.setDeliveryOptionOnShippingCartLines,
            setContactOnShippingCartLines: this.setContactOnShippingCartLines,
            updateCartAttributes: this.updateCartAttributes,
            updateCurrentCart: this.updateCurrentCart,
            getSmartyAddress: this.getSmartyAddress,
            updateCheckoutError: this.updateCheckoutError
        };
        return this.props.renderView(viewProps);
    }

    private getSmartyAddress = async (context: ICoreContext, address: Address) => {
        return getSmartyStreetAddress(context, address);
    };

    private saveShippingAddress = async (
        context: ICoreContext,
        resources: ILdsDeliveryResources,
        telemetry: ITelemetry,
        addressToSave: Address,
        customerAddresses: Address[],
        cartId: string,
        shippingGroup: ICartLineWithProduct[],
        addAddressToAccount: boolean,
        countryRegionId: string,
        cart?: any
    ) => {
        const newCart = await submitAddress(
            context,
            resources,
            telemetry,
            addressToSave,
            customerAddresses,
            cartId,
            shippingGroup,
            addAddressToAccount,
            countryRegionId,
            this.props.config,
            cart
        );
        if (newCart && newCart.CustomerId) {
            this.getShippingCartLines(newCart.CartLines || []);
            this.updateCurrentCart(newCart);
            await this.getDeliveryOptions();
        } else {
            this.setState({ checkoutError: true });
        }
        return newCart;
    };

    private setDeliveryOptionOnShippingCartLines = async (deliveryOption: DeliveryOption | undefined) => {
        //@ts-ignore
        const newCartLine = this.state.currentCart?.CartLines.map(cartLine => {
            cartLine.DeliveryMode = deliveryOption ? deliveryOption.Code : this.props.config.deliveryMode;

            return cartLine;
        });
        let cartLineSubscriptions;
        if (newCartLine && newCartLine[0].ExtensionProperties && newCartLine[0].ExtensionProperties[0]) {
            cartLineSubscriptions = newCartLine?.filter(cartLine => {
                return cartLine.ExtensionProperties![0].Value?.BooleanValue === true;
            });
        }

        await updateCartLinesAsync(
            { callerContext: this.props.context.actionContext },
            this.state.currentCart!.Id,
            cartLineSubscriptions ? cartLineSubscriptions : newCartLine
        );

        const lineDeliverySpecifications = this.state.shippingCartLines?.map(line => {
            return {
                LineId: line.LineId,
                DeliverySpecification: {
                    DeliveryAddress: line.ShippingAddress,
                    DeliveryModeId: deliveryOption ? deliveryOption?.Code : line.DeliveryMode ? line.DeliveryMode : '',
                    DeliveryPreferenceTypeValue: 1
                }
            };
        });
        return updateLineDeliverySpecifications(
            { callerContext: this.props.context.actionContext },
            this.state.checkout?.checkoutCart.cart.Id || '',
            lineDeliverySpecifications || []
        );
    };

    private setContactOnShippingCartLines = async (email: string | undefined, phone: string | undefined) => {
        const checkout = this.state.checkout;
        let address: any;
        if (checkout && checkout.checkoutCart && checkout.checkoutCart.cart && checkout.checkoutCart.cart.CartLines) {
            address = checkout.checkoutCart.cart.CartLines[0].ShippingAddress;
        }
        if (address) {
            address.Phone = phone;
            address.Email = email;
        }

        checkout?.checkoutCart.updateShippingAddress({ newShippingAddress: address });
    };

    private updateCartAttributes = async (attributes: AttributeValueBase[]) => {
        const checkout = this.state.checkout;
        const currentAttributeValues = checkout?.checkoutCart.attributeValues || [];
        const newAttributeValues = [...currentAttributeValues];

        attributes.forEach(x => {
            const matchIndex = newAttributeValues.findIndex(attr => attr.Name === x.Name);
            if (matchIndex > -1) {
                // eslint-disable-next-line security/detect-object-injection
                newAttributeValues[matchIndex] = x;
            } else {
                newAttributeValues.push(x);
            }
        });

        return checkout?.checkoutCart.updateAttributeValues({
            newAttributeValues: newAttributeValues
        });
    };

    private updateCurrentCart = (newCart: Cart) => {
        this.setState({ currentCart: newCart });
    };
    private updateCheckoutError = (value: boolean) => {
        this.setState({ checkoutError: value });
    };

    private readonly getItems = (): React.ReactNode[] => {
        const { slots } = this.props;
        return (slots && slots.primary && slots.primary.length && slots.primary) || [];
    };

    private readonly getShippingCartLines = (cartLines: CartLine[]) => {
        const shippingCartLines: CartLine[] = getCartLinesforDelivery(cartLines);
        this.setState({ shippingCartLines });
    };

    private readonly getDeliveryOptions = async () => {
        const lines = this.state.shippingCartLines || [];
        const checkout = this.state.checkout;
        const shippingCartLineIds: string[] = [];
        lines.map(line => shippingCartLineIds.push(line.LineId || ''));

        if (lines.length > 0 && lines[0].ShippingAddress) {
            const response = await getDeliveryOptionsAsync(
                { callerContext: this.props.context.actionContext },
                checkout?.checkoutCart.cart.Id || '',
                lines[0].ShippingAddress,
                shippingCartLineIds,
                FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode,
                false
            );
            if (response && response.length > 0) {
                let sorted: DeliveryOption[] = [];
                if (response.length > 1) {
                    sorted = response.sort((a, b) => ((a.ShippingChargeAmount || 0) > (b.ShippingChargeAmount || 0) ? 1 : -1));
                } else {
                    sorted = response;
                }
                this.setState({ deliveryOptions: sorted });
            } else {
                this.setState({ deliveryOptions: [{ RecordId: 0 }] });
            }
        }
    };

    private readonly getSubscriptionLines = () => {
        const subscriptionCartLines: CartLine[] = getCartLinesforSubscription(this.state.checkout?.checkoutCart.cart.CartLines || []);

        return subscriptionCartLines;
    };

    private readonly getPickupLines = () => {
        const pickupCartLines: CartLine[] = getCartLinesforPickup(this.state.checkout?.checkoutCart.cart.CartLines || []);

        return pickupCartLines;
    };

    @action
    private readonly init = async (): Promise<void> => {
        const checkout = this.props.data.checkout.result;
        const address = this.props.data.address?.result;
        const countryRegions = this.props.data.countryRegions?.result;
        const addressPurposes = this.props.data.addressPurposes?.result;
        const countryStates = this.props.data.countryStates?.result;
        const churchRetailChannelCountry = this.props.data.churchRetailChannelCountry?.result;
        const customerSubscriptions = this.props.data.customerSubscriptions?.result;

        this.setState({ checkout });
        this.setState({ address });
        this.setState({ countryRegions });
        this.setState({ addressPurposes });
        this.setState({ countryStates });
        this.setState({ currentCart: (await this.props.data.checkout).checkoutCart.cart });
        this.setState({ churchRetailChannelCountry });
        this.setState({ customerSubscriptions });

        this.getShippingCartLines(checkout?.checkoutCart.cart.CartLines || []);
    };
}

export default LdsCheckout;
